import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  EmailOutlined as EmailOutlinedIcon,
  PersonAddAlt as PersonAddAltIcon,
  PhoneIphoneOutlined as PhoneIphoneOutlinedIcon,
} from "@mui/icons-material";
import CirclePickerGrid from "./CirclePickerGrid";
import { NoticeboardButton } from "../shared";

interface AddUserFromSearchEmptyStateProps {
  contactLabel: string;
  circles: React.ComponentProps<typeof CirclePickerGrid>["circles"];
  selectedCircleIds: string[];
  onToggleCircle: (circleId: string) => void;
  circlesLoading?: boolean;
  onAddUser: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddUserFromSearchEmptyState: React.FC<AddUserFromSearchEmptyStateProps> = ({
  contactLabel,
  circles,
  selectedCircleIds,
  onToggleCircle,
  circlesLoading = false,
  onAddUser,
}) => {
  const theme = useTheme();
  const trimmedContact = contactLabel.trim();
  const isEmail = EMAIL_PATTERN.test(trimmedContact);

  const headline = useMemo(
    () =>
      isEmail
        ? "No user found with this email"
        : "No user found with this number",
    [isEmail]
  );

  const selectedCount = selectedCircleIds.length;

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
        px: { xs: 0, sm: 1 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 640,
          mx: "auto",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            pt: { xs: 3, sm: 3.5 },
            pb: { xs: 2.5, sm: 3 },
            textAlign: "center",
            background: `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.06
            )} 0%, transparent 100%)`,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <PersonAddAltIcon sx={{ fontSize: 28 }} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontSize: { xs: "1.05rem", sm: "1.125rem" },
              mb: 0.75,
            }}
          >
            {headline}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.5, maxWidth: 420, mx: "auto" }}
          >
            Not in active users, pending invitations, or join requests. You can
            invite them below.
          </Typography>

          <Chip
            icon={
              isEmail ? (
                <EmailOutlinedIcon sx={{ fontSize: "18px !important" }} />
              ) : (
                <PhoneIphoneOutlinedIcon sx={{ fontSize: "18px !important" }} />
              )
            }
            label={trimmedContact}
            sx={{
              height: 36,
              px: 0.5,
              fontWeight: 600,
              fontSize: "0.9375rem",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.22),
              color: "text.primary",
              "& .MuiChip-icon": {
                color: "primary.main",
              },
            }}
          />
        </Box>

        {circles.length > 0 || circlesLoading ? (
          <>
            <Divider />
            <Box
              sx={{
                px: { xs: 2.5, sm: 3.5 },
                py: { xs: 2.5, sm: 3 },
                bgcolor: alpha(theme.palette.action.hover, 0.35),
              }}
            >
              <CirclePickerGrid
                circles={circles}
                selectedCircleIds={selectedCircleIds}
                onToggle={onToggleCircle}
                isLoading={circlesLoading}
                embedded
                title="Add to circles"
                helperText="Optional — pick now or choose more in the next step."
              />
            </Box>
          </>
        ) : null}

        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2.5, sm: 3 },
            borderTop: circles.length > 0 || circlesLoading ? "none" : undefined,
          }}
        >
          <NoticeboardButton
            variant="primary"
            onClick={onAddUser}
            sx={{
              width: "100%",
              py: 1.25,
              fontSize: "0.9375rem",
              fontWeight: 600,
            }}
          >
            Add this user
            {selectedCount > 0
              ? ` · ${selectedCount} circle${selectedCount > 1 ? "s" : ""}`
              : ""}
          </NoticeboardButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddUserFromSearchEmptyState;
